// Static, presentation-only page content for the public event page.
//
// The event's *form* (fields, consent, status) comes from the DB (public_event_view).
// This file carries the richer marketing copy the design shows — eyebrow, headline,
// schedule, venue, run-of-day — which is not (yet) modelled in the events schema.
// Ported from the restomag-website events module so the two stay visually in sync.
// Keyed by slug; returns null for any event we don't have bespoke content for.

export interface ScheduleItem {
  /** Display time, e.g. "8:00 – 10:00am" or "11:00am+". */
  time: string;
  title: string;
  /** Optional location/note shown under the title. */
  detail?: string;
}

export interface EventHost {
  name: string;
  /** External site the host brand links to. */
  url?: string;
  /** Public-path circular host icon. */
  icon?: string;
}

export interface EventContent {
  /** Eyebrow year, e.g. "2026". */
  year: string;
  /** Eyebrow lead-in before the year, e.g. "Canada Day". */
  occasion: string;
  /** Headline lead; the prize phrase renders after it as the accent. */
  headlineLead: string;
  /** Prize phrase — headline accent + grand-prize value. */
  prize: string;
  blurb: string;
  /** ISO 8601 with explicit Pacific offset. */
  startsAt: string;
  endsAt: string;
  venue: string;
  address: string;
  /** When the draw winner is announced, e.g. "June 20". */
  winnerAnnounced: string;
  host: EventHost;
  schedule: ScheduleItem[];
}

const EYEKANDY_OPENHOUSE_2026: EventContent = {
  year: "2026",
  occasion: "Canada Day",
  headlineLead: "Win a",
  prize: "$500 paint & detail package",
  blurb:
    "Make your Canada Day one to remember. Roll out with Cars & Coffee, join the Tsawwassen car cruise, then cap the day at the Eye Kandy Kustoms Open House. Drop your details for a shot at the grand prize.",
  startsAt: "2026-07-01T08:00:00-07:00",
  endsAt: "2026-07-01T15:00:00-07:00",
  venue: "Eye Kandy Kustoms",
  address: "644 Derwent Way, Delta · Annacis Island",
  winnerAnnounced: "June 20",
  host: { name: "Eye Kandy Kustoms", url: "https://eyekandykustoms.com/", icon: "/eyekandy-icon.webp" },
  schedule: [
    { time: "8:00 – 10:00am", title: "Cars & Coffee", detail: "Tsawwassen Commons A&W" },
    { time: "10:00am", title: "Cruise departure" },
    { time: "10:00 – 11:30am", title: "The Tsawwassen Car Cruise" },
    { time: "11:00am+", title: "Eye Kandy Kustoms Open House", detail: "644 Derwent Way, Delta · Annacis Island" },
  ],
};

const CONTENT_BY_SLUG: Record<string, EventContent> = {
  "eyekandy-openhouse-2026": EYEKANDY_OPENHOUSE_2026,
};

/** Bespoke page content for a slug, or null if we only have the generic DB-driven form. */
export function getEventContent(slug: string): EventContent | null {
  return CONTENT_BY_SLUG[slug] ?? null;
}

const VANCOUVER_TZ = "America/Vancouver";

/** "Wednesday, July 1" — weekday + month + day, pinned to Vancouver. */
export function formatEventDay(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: VANCOUVER_TZ,
  }).format(new Date(iso));
}

/** Compact Vancouver time, e.g. "8am" or "3:30pm". */
function formatCompactTime(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: VANCOUVER_TZ,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const period = get("dayPeriod").toLowerCase().replace(/[.\s]/g, "");
  return get("minute") === "00" ? `${get("hour")}${period}` : `${get("hour")}:${get("minute")}${period}`;
}

/** "8am – 3pm" — compact Vancouver time range. */
export function formatTimeRange(startIso: string, endIso: string): string {
  return `${formatCompactTime(startIso)} – ${formatCompactTime(endIso)}`;
}
